import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  InputAdornment,
  MenuItem,
  Pagination,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import {
  Add,
  Assignment,
  Business,
  CheckCircle,
  FilterList,
  HourglassTop,
  MailOutline,
  Refresh,
  Search,
  Task,
} from '@mui/icons-material';
import {
  PageHeader,
  StatCard,
  FilterBar,
  FilterSpacer,
  TableSurface,
  headerCellSx,
  bodyRowSx,
  CodeCell,
  TypePill,
  StatusDot,
  StatusTone,
  TableEmptyState,
} from '../../components/common/pageKit';
import vendorOnboardingService, {
  ApplicantFields,
  MSME_CATEGORIES,
  SummaryData,
  VENDOR_TYPES,
  VendorOnboardingApplication,
  VendorOnboardingStatus,
  VendorType,
} from '../../services/vendorOnboardingService';

const STATUS_OPTIONS: VendorOnboardingStatus[] = [
  'DRAFT',
  'INVITED',
  'SUBMITTED',
  'UNDER_VERIFICATION',
  'PENDING_APPROVAL',
  'APPROVED',
  'REJECTED',
];

const STATUS_TONE: Record<VendorOnboardingStatus, StatusTone> = {
  DRAFT: 'neutral',
  INVITED: 'info',
  SUBMITTED: 'info',
  UNDER_VERIFICATION: 'warning',
  PENDING_APPROVAL: 'warning',
  APPROVED: 'success',
  REJECTED: 'error',
};

const STATUS_LABEL: Record<VendorOnboardingStatus, string> = {
  DRAFT: 'Draft',
  INVITED: 'Invited',
  SUBMITTED: 'Submitted',
  UNDER_VERIFICATION: 'Under Verification',
  PENDING_APPROVAL: 'Pending Approval',
  APPROVED: 'Approved',
  REJECTED: 'Rejected',
};

const VENDOR_TYPE_TINT: Record<VendorType, string> = {
  INDIVIDUAL: '#0891b2',
  COMPANY: '#4f46e5',
  HUF: '#a16207',
  PARTNERSHIP: '#0d9488',
  LLP: '#7c3aed',
  TRUST: '#be185d',
};

const emptyForm: ApplicantFields = {
  legalName: '',
  tradeName: '',
  contactPerson: '',
  email: '',
  phone: '',
  vendorType: 'COMPANY',
  gstin: '',
  pan: '',
  udyamNumber: '',
  msmeCategory: undefined,
};

const VendorOnboardingPage: React.FC = () => {
  const navigate = useNavigate();

  const [applications, setApplications] = useState<VendorOnboardingApplication[]>([]);
  const [summary, setSummary] = useState<SummaryData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [status, setStatus] = useState<VendorOnboardingStatus | ''>('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState<ApplicantFields>(emptyForm);
  const [createError, setCreateError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);

  const fetchSummary = useCallback(async () => {
    try {
      const data = await vendorOnboardingService.summary();
      setSummary(data);
    } catch (err) {
      // Stat cards simply don't render — the table is still usable without them.
      console.error('Failed to load vendor onboarding summary:', err);
    }
  }, []);

  const fetchList = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await vendorOnboardingService.list({
        status: status || undefined,
        search: search || undefined,
        page,
        limit: 20,
      });
      setApplications(result.applications);
      setTotalPages(result.pagination.totalPages || 1);
    } catch (err: any) {
      setError(err.message || 'Failed to load vendor onboarding applications');
    } finally {
      setLoading(false);
    }
  }, [status, search, page]);

  useEffect(() => {
    fetchSummary();
  }, [fetchSummary]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  const handleCreate = async () => {
    setCreateError(null);
    if (!form.legalName?.trim() || !form.email?.trim()) {
      setCreateError('Legal name and email are required');
      return;
    }
    setCreating(true);
    try {
      const application = await vendorOnboardingService.create(form);
      setCreateOpen(false);
      setForm(emptyForm);
      navigate(`/procurement/vendor-onboarding/${application.id}`);
    } catch (err: any) {
      setCreateError(err.message || 'Failed to create application');
    } finally {
      setCreating(false);
    }
  };

  const total = summary?.total ?? null;
  const byStatus = summary?.byStatus;

  return (
    <Box>
      <PageHeader
        title="Vendor Onboarding"
        subtitle="KYC intake, statutory verification and approval for new vendors"
        actions={
          <>
            <Button
              variant="outlined"
              startIcon={<Refresh />}
              onClick={() => {
                fetchSummary();
                fetchList();
              }}
              disabled={loading}
            >
              Refresh
            </Button>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => {
                setForm(emptyForm);
                setCreateError(null);
                setCreateOpen(true);
              }}
            >
              New Application
            </Button>
          </>
        }
      />

      {summary && (
        <Grid container spacing={2} sx={{ mb: 2.5 }}>
          <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
            <StatCard title="Total Applications" value={total} icon={<Business />} color="#4f46e5" />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
            <StatCard
              title="Under Verification"
              value={byStatus?.UNDER_VERIFICATION ?? 0}
              icon={<HourglassTop />}
              color="#d97706"
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
            <StatCard
              title="Pending Approval"
              value={byStatus?.PENDING_APPROVAL ?? 0}
              icon={<Task />}
              color="#0891b2"
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
            <StatCard
              title="Approved"
              value={byStatus?.APPROVED ?? 0}
              icon={<CheckCircle />}
              color="#16a34a"
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
            <StatCard
              title="Documents Expiring (30d)"
              value={summary.documentsExpiringSoon}
              icon={<Assignment />}
              color="#dc2626"
            />
          </Grid>
        </Grid>
      )}

      <FilterBar>
        <TextField
          select
          size="small"
          label="Status"
          value={status}
          onChange={(e) => {
            setPage(1);
            setStatus(e.target.value as VendorOnboardingStatus | '');
          }}
          sx={{ minWidth: 220 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <FilterList sx={{ fontSize: 18, color: 'text.disabled' }} />
              </InputAdornment>
            ),
          }}
        >
          <MenuItem value="">All Statuses</MenuItem>
          {STATUS_OPTIONS.map((s) => (
            <MenuItem key={s} value={s}>
              {STATUS_LABEL[s]}
            </MenuItem>
          ))}
        </TextField>

        <TextField
          size="small"
          label="Search"
          placeholder="Legal name, email, reference..."
          value={search}
          onChange={(e) => {
            setPage(1);
            setSearch(e.target.value);
          }}
          sx={{ minWidth: 260 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search sx={{ fontSize: 18, color: 'text.disabled' }} />
              </InputAdornment>
            ),
          }}
        />

        <FilterSpacer />

        <Typography variant="body2" color="text.secondary">
          Showing <strong>{applications.length}</strong> of <strong>{total ?? '—'}</strong>
        </Typography>
      </FilterBar>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableSurface>
          <Table>
            <TableHead>
              <TableRow>
                {['Reference', 'Applicant', 'Type', 'MSME', 'Status', 'Created', ''].map((h, i) => (
                  <TableCell key={h || i} sx={headerCellSx} align={i === 6 ? 'right' : 'left'}>
                    {h}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {applications.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} sx={{ border: 0 }}>
                    <TableEmptyState
                      title="No applications match these filters"
                      hint="Clear the status or search filter, or create a new application."
                    />
                  </TableCell>
                </TableRow>
              ) : (
                applications.map((app) => (
                  <TableRow
                    key={app.id}
                    sx={{ ...bodyRowSx, cursor: 'pointer' }}
                    onClick={() => navigate(`/procurement/vendor-onboarding/${app.id}`)}
                  >
                    <TableCell sx={{ py: 1.5 }}>
                      <CodeCell code={app.reference_no} tint={VENDOR_TYPE_TINT[app.vendor_type]} icon={<Business />} />
                    </TableCell>
                    <TableCell sx={{ py: 1.5 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {app.legal_name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {app.email}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ py: 1.5 }}>
                      <TypePill label={app.vendor_type} tint={VENDOR_TYPE_TINT[app.vendor_type]} />
                    </TableCell>
                    <TableCell sx={{ py: 1.5 }}>
                      {app.msme_category ? (
                        <TypePill
                          label={app.msme_category.replace('_', ' ')}
                          tint={app.msme_category === 'NOT_REGISTERED' ? '#64748b' : '#0d9488'}
                        />
                      ) : (
                        <Typography variant="caption" color="text.disabled">
                          —
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell sx={{ py: 1.5 }}>
                      <StatusDot label={STATUS_LABEL[app.status]} tone={STATUS_TONE[app.status]} />
                    </TableCell>
                    <TableCell sx={{ py: 1.5 }}>
                      <Typography variant="body2" color="text.secondary">
                        {new Date(app.created_at).toLocaleDateString('en-IN')}
                      </Typography>
                    </TableCell>
                    <TableCell align="right" sx={{ py: 1.5 }}>
                      <Button size="small">View</Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableSurface>
      )}

      {totalPages > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2.5 }}>
          <Pagination count={totalPages} page={page} onChange={(_, p) => setPage(p)} color="primary" />
        </Box>
      )}

      {/* Create application dialog */}
      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>New Vendor Onboarding Application</DialogTitle>
        <DialogContent dividers>
          {createError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {createError}
            </Alert>
          )}
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                label="Legal Name *"
                fullWidth
                value={form.legalName}
                onChange={(e) => setForm({ ...form, legalName: e.target.value })}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                label="Trade Name"
                fullWidth
                value={form.tradeName || ''}
                onChange={(e) => setForm({ ...form, tradeName: e.target.value })}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                label="Email *"
                type="email"
                fullWidth
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                label="Phone"
                fullWidth
                value={form.phone || ''}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                label="Contact Person"
                fullWidth
                value={form.contactPerson || ''}
                onChange={(e) => setForm({ ...form, contactPerson: e.target.value })}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                select
                label="Vendor Type"
                fullWidth
                value={form.vendorType}
                onChange={(e) => setForm({ ...form, vendorType: e.target.value as VendorType })}
              >
                {VENDOR_TYPES.map((t) => (
                  <MenuItem key={t} value={t}>
                    {t}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                label="GSTIN"
                fullWidth
                value={form.gstin || ''}
                onChange={(e) => setForm({ ...form, gstin: e.target.value.toUpperCase() })}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                label="PAN"
                fullWidth
                value={form.pan || ''}
                onChange={(e) => setForm({ ...form, pan: e.target.value.toUpperCase() })}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                label="UDYAM Number"
                fullWidth
                value={form.udyamNumber || ''}
                onChange={(e) => setForm({ ...form, udyamNumber: e.target.value.toUpperCase() })}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                select
                label="MSME Category"
                fullWidth
                value={form.msmeCategory || ''}
                onChange={(e) =>
                  setForm({ ...form, msmeCategory: (e.target.value || undefined) as ApplicantFields['msmeCategory'] })
                }
              >
                <MenuItem value="">Not specified</MenuItem>
                {MSME_CATEGORIES.map((c) => (
                  <MenuItem key={c} value={c}>
                    {c.replace('_', ' ')}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)}>Cancel</Button>
          <Button variant="contained" startIcon={<MailOutline />} onClick={handleCreate} disabled={creating}>
            {creating ? 'Creating...' : 'Create Application'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default VendorOnboardingPage;
